import React, { useState, useEffect, useRef } from 'react';
import {
  FaStar,
  FaRegStar,
  FaRoute,
  FaUtensils,
  FaHiking,
  FaClock,
  FaThumbsUp,
  FaThumbsDown,
  FaCheck,
  FaHeart,
  FaEdit,
  FaTrash
} from 'react-icons/fa';
import { submitRating, getRatingSummary, deleteRating } from '../api/client';
import ConfirmDialog from './ConfirmDialog';

// Emoji options with their display values
const EMOJI_OPTIONS = [
  { id: 'loved', emoji: '😍', label: 'Loved it!', color: 'text-pink-500', bgColor: 'bg-pink-50', borderColor: 'border-pink-300', hoverBg: 'hover:bg-pink-100' },
  { id: 'good', emoji: '👍', label: 'Good', color: 'text-green-500', bgColor: 'bg-green-50', borderColor: 'border-green-300', hoverBg: 'hover:bg-green-100' },
  { id: 'okay', emoji: '😐', label: 'Okay', color: 'text-yellow-500', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-300', hoverBg: 'hover:bg-yellow-100' },
  { id: 'poor', emoji: '👎', label: 'Needs work', color: 'text-red-500', bgColor: 'bg-red-50', borderColor: 'border-red-300', hoverBg: 'hover:bg-red-100' },
];

// What users liked - chips
const LIKED_ASPECTS = [
  { id: 'route_planning', label: 'Route Planning', icon: FaRoute },
  { id: 'restaurant_picks', label: 'Restaurant Picks', icon: FaUtensils },
  { id: 'timing', label: 'Timing & Schedule', icon: FaClock },
  { id: 'hidden_gems', label: 'Hidden Gems', icon: FaHeart },
  { id: 'activities', label: 'Activities', icon: FaHiking },
  { id: 'budget_friendly', label: 'Budget Friendly', icon: FaThumbsUp },
];

// Areas for improvement - chips
const IMPROVEMENT_AREAS = [
  { id: 'more_options', label: 'More Options' },
  { id: 'better_timing', label: 'Better Timing' },
  { id: 'different_cuisine', label: 'Different Cuisine' },
  { id: 'shorter_drives', label: 'Shorter Drives' },
  { id: 'more_activities', label: 'More Activities' },
  { id: 'budget_options', label: 'Budget Options' },
];

// Star Rating Component
const StarRating = ({ rating, setRating, size = 'text-2xl', readonly = false }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`${size} transition-all duration-150 ${
            readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'
          }`}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          onClick={() => !readonly && setRating(star)}
        >
          {(hoverRating || rating) >= star ? (
            <FaStar className="text-yellow-400 drop-shadow-sm" />
          ) : (
            <FaRegStar className="text-neutral-300" />
          )}
        </button>
      ))}
    </div>
  );
};

// Mini Star Rating for display
const MiniStarRating = ({ rating, size = 'text-sm' }) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={size}>
          {rating >= star ? (
            <FaStar className="text-yellow-400" />
          ) : rating >= star - 0.5 ? (
            <FaStar className="text-yellow-400 opacity-50" />
          ) : (
            <FaRegStar className="text-neutral-300" />
          )}
        </span>
      ))}
    </div>
  );
};

// Category Rating Component
const CategoryRating = ({ label, icon: Icon, rating, setRating }) => {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2 text-neutral-700">
        <Icon className="text-primary-500" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <StarRating rating={rating} setRating={setRating} size="text-lg" />
    </div>
  );
};

// Chip Selection Component
const ChipSelector = ({ options, selected, setSelected, colorScheme = 'primary' }) => {
  const toggleChip = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(s => s !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const colors = {
    primary: {
      selected: 'bg-primary-500 text-white border-primary-500',
      unselected: 'bg-white text-neutral-600 border-neutral-300 hover:border-primary-400 hover:bg-primary-50'
    },
    secondary: {
      selected: 'bg-secondary-500 text-white border-secondary-500',
      unselected: 'bg-white text-neutral-600 border-neutral-300 hover:border-secondary-400 hover:bg-secondary-50'
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected.includes(option.id);
        const Icon = option.icon;
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => toggleChip(option.id)}
            className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
              isSelected ? colors[colorScheme].selected : colors[colorScheme].unselected
            }`}
          >
            {Icon && <Icon className="text-xs" />}
            {option.label}
            {isSelected && <FaCheck className="text-xs ml-1" />}
          </button>
        );
      })}
    </div>
  );
};

// Main Rating Feedback Component
const RatingFeedback = ({ requestId, onRatingSubmitted, compact = false, tripStartDate = null, tripEndDate = null }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showRatingPopup, setShowRatingPopup] = useState(false);

  // Form state
  const [emojiReaction, setEmojiReaction] = useState(null);
  const [starRating, setStarRating] = useState(0);
  const [comment, setComment] = useState('');
  const [likedAspects, setLikedAspects] = useState([]);
  const [improvementAreas, setImprovementAreas] = useState([]);
  const [routeRating, setRouteRating] = useState(0);
  const [foodRating, setFoodRating] = useState(0);
  const [activitiesRating, setActivitiesRating] = useState(0);
  const [timingRating, setTimingRating] = useState(0);
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [tripCompleted, setTripCompleted] = useState(false);

  // Confirm dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);
  const popupShownRef = useRef(false);

  // Calculate trip status - parse dates as local dates (not UTC)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    // Handle both Date objects and strings
    if (dateStr instanceof Date) return dateStr;
    // Parse YYYY-MM-DD as local date (not UTC)
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    return new Date(dateStr);
  };

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const tripStart = parseLocalDate(tripStartDate);
  if (tripStart) tripStart.setHours(0, 0, 0, 0);

  const tripEnd = parseLocalDate(tripEndDate);
  if (tripEnd) tripEnd.setHours(0, 0, 0, 0);

  // Determine trip status
  // - Before trip: we have a start date and it's in the future
  // - During trip: started but not ended (or no end date)
  // - After trip: we have an end date and it's in the past
  const tripHasStarted = tripStart ? now >= tripStart : true;  // No start date = assume started
  const tripHasEnded = tripEnd ? now > tripEnd : false;  // No end date = not ended yet
  const tripIsUpcoming = tripStart && now < tripStart;  // Trip hasn't started yet

  // Cleanup on unmount to prevent state updates after navigation
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load existing rating
  useEffect(() => {
    loadRatingSummary();
  }, [requestId]);

  // Show rating popup after trip ends if user hasn't rated
  useEffect(() => {
    if (!loading && tripHasEnded && !summary?.user_rating && !popupShownRef.current) {
      // Check localStorage to see if popup was dismissed for this trip
      const dismissedKey = `rating_popup_dismissed_${requestId}`;
      const wasDismissed = localStorage.getItem(dismissedKey);

      if (!wasDismissed) {
        setShowRatingPopup(true);
        popupShownRef.current = true;
      }
    }
  }, [loading, tripHasEnded, summary, requestId]);

  const dismissPopup = () => {
    setShowRatingPopup(false);
    // Remember that user dismissed this popup
    localStorage.setItem(`rating_popup_dismissed_${requestId}`, 'true');
  };

  const handlePopupRate = () => {
    setShowRatingPopup(false);
    // Scroll to rating section or just close popup to show the form
  };

  const loadRatingSummary = async () => {
    try {
      setLoading(true);
      const data = await getRatingSummary(requestId);

      // Only update state if component is still mounted
      if (!isMountedRef.current) return;

      setSummary(data);

      // If user has existing rating, populate form
      if (data.user_rating) {
        const r = data.user_rating;
        setEmojiReaction(r.emoji_reaction);
        setStarRating(r.rating || 0);
        setComment(r.comment || '');
        setLikedAspects(r.liked_aspects || []);
        setImprovementAreas(r.improvement_areas || []);
        setRouteRating(r.route_rating || 0);
        setFoodRating(r.food_rating || 0);
        setActivitiesRating(r.activities_rating || 0);
        setTimingRating(r.timing_rating || 0);
        setWouldRecommend(r.would_recommend);
        setTripCompleted(r.trip_completed || false);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Error loading rating summary:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Check if all required fields are filled
  const isFormValid = routeRating > 0 && foodRating > 0 && activitiesRating > 0 && timingRating > 0 &&
    likedAspects.length > 0 && improvementAreas.length > 0 && wouldRecommend !== null;

  const handleSubmit = async () => {
    // Require all mandatory fields
    if (!isFormValid) {
      return; // All fields are required
    }

    // Calculate overall rating as average of category ratings
    const calculatedOverallRating = Math.round((routeRating + foodRating + activitiesRating + timingRating) / 4);

    try {
      setSubmitting(true);
      const ratingData = {
        emoji_reaction: emojiReaction,
        rating: calculatedOverallRating,
        comment: comment || null,
        liked_aspects: likedAspects.length > 0 ? likedAspects : null,
        improvement_areas: improvementAreas.length > 0 ? improvementAreas : null,
        route_rating: routeRating > 0 ? routeRating : null,
        food_rating: foodRating > 0 ? foodRating : null,
        activities_rating: activitiesRating > 0 ? activitiesRating : null,
        timing_rating: timingRating > 0 ? timingRating : null,
        would_recommend: wouldRecommend,
        trip_completed: tripCompleted
      };

      await submitRating(requestId, ratingData);
      await loadRatingSummary();
      setIsEditing(false);

      if (onRatingSubmitted) {
        onRatingSubmitted();
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);

    try {
      setSubmitting(true);
      await deleteRating(requestId);
      // Reset form
      setEmojiReaction(null);
      setStarRating(0);
      setComment('');
      setLikedAspects([]);
      setImprovementAreas([]);
      setRouteRating(0);
      setFoodRating(0);
      setActivitiesRating(0);
      setTimingRating(0);
      setWouldRecommend(null);
      setTripCompleted(false);
      await loadRatingSummary();
    } catch (error) {
      console.error('Error deleting rating:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const hasUserRating = summary?.user_rating;
  const showForm = !hasUserRating || isEditing;

  // Compact view for dashboard cards
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {summary?.average_rating ? (
          <>
            <MiniStarRating rating={summary.average_rating} />
            <span className="text-sm text-neutral-600">
              {summary.average_rating.toFixed(1)} ({summary.total_ratings})
            </span>
          </>
        ) : (
          <span className="text-xs text-neutral-400">No ratings yet</span>
        )}
      </div>
    );
  }

  // Determine title and messaging based on trip status
  const getTitle = () => {
    if (tripHasEnded) return 'Rate Your Completed Trip';
    if (tripIsUpcoming) return 'Rate This Itinerary';
    return 'Rate Your Trip So Far';  // During trip
  };

  const getSubtitle = () => {
    if (tripHasEnded) return 'Your trip has ended! Share your experience to help other travelers.';
    if (tripIsUpcoming) return 'How does this itinerary look? You can update your rating during and after the trip.';
    return 'Your trip is in progress! Share your thoughts so far.';  // During trip
  };

  const getQuestionText = () => {
    if (tripHasEnded) return 'How was your overall experience?';
    if (tripIsUpcoming) return 'How does this itinerary look to you?';
    return 'How is your trip going so far?';  // During trip
  };

  return (
    <div className="bg-white rounded-xl shadow-card p-6">
      {/* Rating Popup Modal - Shows after trip ends */}
      {showRatingPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaStar className="text-3xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-neutral-800 mb-2">How was your trip?</h3>
              <p className="text-neutral-600 mb-6">
                Your trip has ended! We'd love to hear about your experience. Your feedback helps us improve and helps other travelers.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={dismissPopup}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-neutral-600 bg-neutral-100 hover:bg-neutral-200 transition-colors"
                >
                  Maybe Later
                </button>
                <button
                  onClick={handlePopupRate}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 transition-colors"
                >
                  Rate Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-bold text-neutral-800">{getTitle()}</h3>
        {hasUserRating && !isEditing && (
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
            >
              <FaEdit /> Edit
            </button>
            <button
              onClick={handleDelete}
              className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1"
            >
              <FaTrash /> Delete
            </button>
          </div>
        )}
      </div>

      {/* Subtitle based on trip status */}
      {!hasUserRating && (
        <p className="text-sm text-neutral-500 mb-4">{getSubtitle()}</p>
      )}

      {/* Summary Display (when user has rated and not editing) */}
      {hasUserRating && !isEditing && (
        <div className="space-y-4">
          {/* User's Rating */}
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4">
            <p className="text-sm text-neutral-500 mb-2">Your Rating</p>
            <div className="flex items-center gap-4">
              {summary.user_rating.emoji_reaction && (
                <span className="text-3xl">
                  {EMOJI_OPTIONS.find(e => e.id === summary.user_rating.emoji_reaction)?.emoji}
                </span>
              )}
              {summary.user_rating.rating && (
                <div className="flex items-center gap-2">
                  <StarRating rating={summary.user_rating.rating} readonly size="text-xl" />
                  <span className="text-neutral-700 font-semibold">{summary.user_rating.rating}/5</span>
                </div>
              )}
            </div>
            {summary.user_rating.comment && (
              <p className="mt-3 text-neutral-600 text-sm italic">"{summary.user_rating.comment}"</p>
            )}
          </div>

          {/* Aggregate Stats */}
          {summary.total_ratings > 1 && (
            <div className="border-t pt-4">
              <p className="text-sm text-neutral-500 mb-2">All Ratings ({summary.total_ratings} total)</p>
              <div className="flex items-center gap-4">
                {summary.average_rating && (
                  <div className="flex items-center gap-2">
                    <MiniStarRating rating={summary.average_rating} size="text-lg" />
                    <span className="text-neutral-700 font-semibold">{summary.average_rating.toFixed(1)}</span>
                  </div>
                )}
                {/* Emoji breakdown */}
                <div className="flex gap-2">
                  {EMOJI_OPTIONS.map(opt => (
                    summary.emoji_counts[opt.id] > 0 && (
                      <span key={opt.id} className="text-sm">
                        {opt.emoji} {summary.emoji_counts[opt.id]}
                      </span>
                    )
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rating Form */}
      {showForm && (
        <div className="space-y-6">
          {/* Quick Emoji Reaction */}
          <div>
            <p className="text-sm font-medium text-neutral-700 mb-3">
              {getQuestionText()}
            </p>
            <div className="flex flex-wrap gap-3">
              {EMOJI_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setEmojiReaction(option.id)}
                  className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-200 ${
                    emojiReaction === option.id
                      ? `${option.bgColor} ${option.borderColor} scale-105`
                      : `bg-white border-neutral-300 ${option.hoverBg}`
                  }`}
                >
                  <span className="text-3xl mb-1">{option.emoji}</span>
                  <span className={`text-xs font-medium ${emojiReaction === option.id ? option.color : 'text-neutral-500'}`}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Appreciation Message */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Thank you for taking the time to share your feedback!</span> Your insights help us improve our recommendations and assist fellow travelers in planning their perfect trips.
            </p>
          </div>

          {/* Detailed Feedback Section - Always Visible */}
          <div className="space-y-6 pt-2 border-t">
              {/* What did you like? */}
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-3">
                  {tripIsUpcoming ? 'What looks good about this plan?' : 'What did you like?'} <span className="text-red-500">*</span>
                </p>
                <ChipSelector
                  options={LIKED_ASPECTS}
                  selected={likedAspects}
                  setSelected={setLikedAspects}
                  colorScheme="secondary"
                />
              </div>

              {/* What could improve? */}
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-3">
                  {tripIsUpcoming ? 'What would you change?' : 'What could be better?'} <span className="text-red-500">*</span>
                </p>
                <ChipSelector
                  options={IMPROVEMENT_AREAS}
                  selected={improvementAreas}
                  setSelected={setImprovementAreas}
                  colorScheme="primary"
                />
              </div>

              {/* Category Ratings */}
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-2">Rate Specific Aspects <span className="text-red-500">*</span></p>
                <div className="bg-neutral-50 rounded-lg p-3 space-y-1">
                  <CategoryRating label="Route & Navigation" icon={FaRoute} rating={routeRating} setRating={setRouteRating} />
                  <CategoryRating label="Food & Restaurants" icon={FaUtensils} rating={foodRating} setRating={setFoodRating} />
                  <CategoryRating label="Activities & Attractions" icon={FaHiking} rating={activitiesRating} setRating={setActivitiesRating} />
                  <CategoryRating label="Timing & Schedule" icon={FaClock} rating={timingRating} setRating={setTimingRating} />
                </div>
              </div>

              {/* Would Recommend */}
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-3">Would you recommend this itinerary? <span className="text-red-500">*</span></p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setWouldRecommend(true)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      wouldRecommend === true
                        ? 'bg-green-50 border-green-400 text-green-700'
                        : 'bg-white border-neutral-300 text-neutral-600 hover:border-green-300'
                    }`}
                  >
                    <FaThumbsUp /> Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setWouldRecommend(false)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      wouldRecommend === false
                        ? 'bg-red-50 border-red-400 text-red-700'
                        : 'bg-white border-neutral-300 text-neutral-600 hover:border-red-300'
                    }`}
                  >
                    <FaThumbsDown /> No
                  </button>
                </div>
              </div>

              {/* Trip Completed Checkbox - only show during or after trip */}
              {!tripIsUpcoming && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tripCompleted}
                    onChange={(e) => setTripCompleted(e.target.checked)}
                    className="w-4 h-4 text-primary-500 rounded border-neutral-300 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">I completed this trip</span>
                </label>
              )}
            </div>

          {/* Comment */}
          <div>
            <p className="text-sm font-medium text-neutral-700 mb-2">Additional Comments (Optional)</p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                tripHasEnded
                  ? "Share your experience - highlights, tips for others..."
                  : tripIsUpcoming
                    ? "What do you think of this itinerary plan?"
                    : "How is the trip going? Any thoughts so far..."
              }
              rows={3}
              maxLength={2000}
              className="w-full px-3 py-2 border-2 border-neutral-300 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-primary-500 resize-none text-sm"
            />
            <p className="text-xs text-neutral-400 text-right mt-1">{comment.length}/2000</p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={submitting || !isFormValid}
              className={`flex-1 py-3 px-6 rounded-xl font-semibold text-white transition-all duration-200 ${
                submitting || !isFormValid
                  ? 'bg-neutral-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 shadow-travel hover:shadow-elevated'
              }`}
            >
              {submitting ? 'Submitting...' : hasUserRating ? 'Update Rating' : 'Submit Rating'}
            </button>
            {isEditing && (
              <button
                onClick={() => setIsEditing(false)}
                className="px-6 py-3 rounded-xl font-semibold text-neutral-600 bg-neutral-100 hover:bg-neutral-200 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Rating"
        message="Are you sure you want to delete your rating? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

// Export components for use elsewhere
export { StarRating, MiniStarRating };
export default RatingFeedback;
